import { createPortal } from 'react-dom';
import { useTranslation } from '@/lib/i18n';
import { Button } from '@/components/ui';
import type { OrderDetail } from '@/lib/api-admin/types';
import { usePrintDocument } from './usePrintDocument';
import type { PrintDocumentKind } from './types';
import { TransactionStatementDocument } from './documents/TransactionStatementDocument';
import { OrderStatementDocument } from './documents/OrderStatementDocument';
import './printDocument.css';

interface PrintDocumentModalProps {
  orderId: string | undefined;
  order: OrderDetail | undefined;
  kind: PrintDocumentKind;
  onClose: () => void;
}

/**
 * 16차 TASK-8 — 출력물 미리보기/인쇄 모달.
 * 화면에서는 오버레이 위 카드로 미리보기, '인쇄' 시 `window.print()` →
 * @media print 가 `.print-surface` 만 A4 한 장으로 남긴다(컨트롤은 .print-hide).
 */
export const PrintDocumentModal = ({
  orderId,
  order,
  kind,
  onClose,
}: PrintDocumentModalProps) => {
  const { t } = useTranslation('orders');
  const query = usePrintDocument({ orderId, order, kind, enabled: true });

  const titleKey = `orders.print.kind.${kind}` as const;

  // ⚠ 오버레이는 createPortal 로 document.body 에 렌더한다. 호출처(주문 상세 sticky footer)는
  // backdrop-filter 를 쓰는데, backdrop-filter 는 position:fixed 자식의 containing block 을 생성한다.
  // 인라인 렌더 시 이 모달(fixed inset-0)이 footer 박스에 갇혀 짤렸다 → portal 로 footer 밖(body)에서
  // 렌더해 viewport 기준으로 펼친다(액션 모달의 Dialog portal 과 동일 패턴).
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t(titleKey)}
      className="print-root fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4"
      style={{ background: 'var(--scrim-modal)' }}
      onClick={onClose}
    >
      <div
        className="my-8 w-full max-w-3xl rounded-xl shadow-[var(--elevation-3)]"
        style={{ background: 'var(--surface-0)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 화면 전용 헤더(인쇄 시 숨김). */}
        <div
          className="print-hide flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: 'var(--divider-soft)' }}
        >
          <h2
            className="text-lg font-semibold"
            style={{ color: 'var(--content-strong)' }}
          >
            {t(titleKey)}
          </h2>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="cta"
              size="sm"
              onClick={() => window.print()}
              disabled={query.isPending || query.isError}
            >
              {t('orders.print.printButton')}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              {t('orders.print.close')}
            </Button>
          </div>
        </div>

        <div className="px-6 py-6">
          {query.isPending ? (
            <p
              className="print-hide py-8 text-center text-sm"
              style={{ color: 'var(--content-muted)' }}
            >
              {t('orders.print.loading')}
            </p>
          ) : query.isError || !query.data ? (
            <p
              className="print-hide py-8 text-center text-sm"
              role="alert"
              style={{ color: 'var(--status-danger-fg)' }}
            >
              {t('orders.print.error')}
            </p>
          ) : query.data.kind === 'transaction-statement' ? (
            <TransactionStatementDocument doc={query.data.doc} />
          ) : (
            <OrderStatementDocument doc={query.data.doc} />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};
